import ApiService from './ApiService';

/**
 * Service for handling part orders.
 */
class PartOrdersService extends ApiService {

    /**
     * Fetches part orders associated with a specific appointment ID.
     * @param {number} appointmentId - The unique identifier for the appointment
     * @returns {Promise<Array>} A promise that resolves to an array of part orders
     * @throws {Error} Throws an error if the request fails or if the appointment ID is missing
     */
    async getPartOrdersByAppointmentId(appointmentId) {
        // Check if the folder ID is provided
        if (!appointmentId) {
            console.error('Appointment ID is missing.');
            throw new Error('L\'identifiant de l\'intervention est manquant.');
        }

        try {
            // Fetch part orders for the given appointment ID
            const partOrders = await this.request('GET', `/part-order/read?id_appointment=${appointmentId}`);

            // Return the part orders or an empty array if none are found
            return partOrders || [];
        } catch (error) {
            throw new Error(error?.message);
        }
    }

    /**
     * Fetches all pending part orders.
     * @returns {Promise<Array>} A promise that resolves to an array of pending part orders
     * @throws {Error} Throws an error if the request fails
     */
    async getPartOrdersPending() {
        try {
            // Fetch pending part orders
            const partOrders = await this.request('GET', '/part-order/read?state=EN_ATTENTE');
            return partOrders || [];
        } catch (error) {
            throw new Error(error?.message);
        }
    }

    /**
     * Fetches all sent part orders.
     * @returns {Promise<Array>} A promise that resolves to an array of sent part orders
     * @throws {Error} Throws an error if the request fails
     */
    async getPartOrdersSent() {
        try {
            // Fetch sent part orders
            const partOrders = await this.request('GET', '/part-order/read?state=TRANSMIS');
            return partOrders || [];
        } catch (error) {
            throw new Error(error?.message);
        }
    }

    /**
     * Creates a new part order.
     * @param {Object} payload - The data for the new action
     * @returns {Promise<Object>} A promise that resolves to the created action object
     * @throws {Error} Throws an error if the request fails
     */
    async createPartOrder(payload) {

        // Check if the payload data is provided
        if (!payload) {
            console.error('Part order data is missing.');
            throw new Error('Les données de la commande de pièce sont manquantes.');
        }

        try {
            // Create a new part order using the provided data
            const response = await this.request('POST', '/part-order/create', payload);
            return response;
        } catch (error) {
            throw new Error(error?.message);
        }
    }

    /**
     * Updates an existing part order by its unique ID.
     * @param {number} partOrderId - The unique identifier for the part order
     * @param {Object} payload - The data to update in the part order
     * @returns {Promise<Object>} A promise that resolves to the updated part order object
     * @throws {Error} Throws an error if the request fails
     */
    async editPartOrder(partOrderId, payload) {
        // Check if the part order ID is provided
        if (!partOrderId) {
            console.error('Part order ID is missing.');
            throw new Error('L\'identifiant de la commande de pièce est manquant.');
        }

        // Check if the payload data is provided
        if (!payload) {
            console.error('Part order data is missing.');
            throw new Error('Les données de la commande de pièce sont manquantes.');
        }

        try {
            // Update the existing part order using the provided data
            const response = await this.request('PUT', `${partOrderId}/part-order/update`, payload);
            return response;
        } catch (error) {
            throw new Error(error?.message);
        }
    }

    /**
     * Deletes a part order by its unique ID.
     * @param {number} partOrderId - The unique identifier for the part order
     * @returns {Promise<void>} A promise that resolves when the part order is deleted
     * @throws {Error} Throws an error if the request fails
     */
    async deletePartOrder(partOrderId) {
        // Check if the part order ID is provided
        if (!partOrderId) {
            console.error('Part order ID is missing.');
            throw new Error('L\'identifiant de la commande de pièce est manquant.');
        }   

        try {
            // Send a DELETE request to the endpoint for deleting a part order
            const response = await this.request('DELETE', `${partOrderId}/part-order/delete`);
            return response;
        } catch (error) {
            throw new Error(error?.message);
        }
    }
}

const partOrdersService = new PartOrdersService;
export default partOrdersService;